import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Chip, Divider,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, Stack,
  Switch, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
  InputAdornment,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import DirectionsTransitIcon from '@mui/icons-material/DirectionsTransit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import { api, type CorridorQuery } from '../api/client';
import { themeName } from '../copy/mission';
import {
  countRoadAnswers, DEFAULT_ROAD_OPTIONS, JourneyCharacterFields,
  withRoadDefaults,
} from './JourneyCharacterFields';
import { PlaceAutocomplete } from './PlaceAutocomplete';
import { PlacePicker } from './PlacePicker';
import { AvoidancePanel, BoostChoice, CategoryChoice } from './AvoidancePanel';
import { DurationField } from './DurationField';
import { SitesModeSwitch, useSitesMode, type SitesMode } from './SitesModeSwitch';
import { BreakFields, BREAK_CATEGORIES } from './BreakFields';
import { FieldGroup } from './FieldGroup';
import { samePlaceProblems } from '../util/samePlace';
import { StayRows } from './StayRows';
import {
  StopRecommendations, useRecommendations, type MapPreview,
} from './StopRecommendations';
import type {
  Category, City, PlanRequest, Poi, RouteStyle, RouteTheme, TravelMode,
} from '../api/types';
import { SELECTABLE_TRAVEL_MODES, MODE_LABELS } from '../api/types';
import { FEATURES } from '../config/features';

const MODE_ICONS: Record<TravelMode, React.ReactNode> = {
  walk: <DirectionsWalkIcon fontSize="small" />,
  bike: <DirectionsBikeIcon fontSize="small" />,
  motorcycle: <TwoWheelerIcon fontSize="small" />,
  car: <DirectionsCarIcon fontSize="small" />,
  taxi: <LocalTaxiIcon fontSize="small" />,
  truck: <LocalShippingIcon fontSize="small" />,
  transit: <DirectionsTransitIcon fontSize="small" />,
};


interface Props {
  /**
   * A travel mode chosen elsewhere — from the "plan it by car" offer on a
   * failed plan. Carries a counter so the same mode can be asked for twice.
   */
  requestedMode?: { mode: TravelMode; token: number } | null;
  city: City;
  styles: RouteStyle[];
  submitting: boolean;
  onSubmit: (request: PlanRequest) => void;
  /*
   * Place selections live in the page, not here, so the map's category
   * browser can add to them too — clicking a museum and choosing "visit"
   * has to land in the same list as typing its name.
   */
  /** The places the traveller named. What the list shows, and asks about. */
  /** Opens the place browser, where the catalogue is read with its coverage. */
  onBrowsePlaces?: () => void;
  mustVisit: Poi[];
  /**
   * Places the round must also keep, which nobody chose.
   *
   * A pinned order is held by naming every stop that makes it, so dragging
   * one row turns the planner's own suggestions into required places. They
   * have to travel in the request — drop them and the order has nothing to
   * hold — and they must stay out of the list above, which is a record of
   * what somebody picked. See `pinnedByOrder` on the page.
   */
  orderHeldPoiIds?: string[];
  onMustVisitChange: (pois: Poi[]) => void;
  avoid: Poi[];
  onAvoidChange: (pois: Poi[]) => void;
  /** Lifted too, so the map can draw the zones before the route is planned. */
  avoidDistanceM: number;
  onAvoidDistanceChange: (metres: number) => void;
  categories: Category[];
  /** How many avoid zones the routing engine will accept at this radius. */
  /**
   * Categories avoided wholesale. Stored as categories, not expanded into
   * places, so "avoid all hospitals" stays one chip and one click to undo.
   */
  avoidCategories: string[];
  onAvoidCategoriesChange: (keys: string[]) => void;
  /** Lifted so a dropped-places suggestion can raise it in one click. */
  availableMinutes: number;
  onAvailableMinutesChange: (minutes: number) => void;
  /*
   * Endpoints live in the page so a spot marked on the map can become the
   * start or destination, not just a stop.
   */
  start: Poi | null;
  onStartChange: (poi: Poi | null) => void;
  startPin: PinnedPoint | null;
  onStartPinChange: (pin: PinnedPoint | null) => void;
  end: Poi | null;
  onEndChange: (poi: Poi | null) => void;
  endPin: PinnedPoint | null;
  onEndPinChange: (pin: PinnedPoint | null) => void;
  /** Coordinate stops with no place behind them. */
  visitPins: PinnedPoint[];
  onVisitPinsChange: (pins: PinnedPoint[]) => void;
  /**
   * Fires whenever the form's request changes, so an existing plan can be
   * re-run without the traveller pressing the button again. Null while the
   * form is not yet complete enough to route.
   */
  onDraftChange?: (draft: PlanRequest | null) => void;
  avoidPins: PinnedPoint[];
  /** Points the path is bent through. Shape only — never stops. */
  viaPoints: { lat: number; lng: number; afterStopIndex: number }[];
  /**
   * A visiting order the traveller arranged by hand, or null to let the
   * optimiser choose. Carried here so that editing anything else does not
   * quietly discard the arrangement.
   */
  pinnedOrder: string[] | null;
  /** Clears the whole form back to empty. */
  onReset?: () => void;
  /** Stay times chosen per stop, keyed by place id or `pin-N`. */
  stopDwellMinutes: Record<string, number>;
  /** Sets one place's stay, or clears it back to its category's default. */
  onStopDwellChange: (poiId: string, minutes: number | null) => void;
  /**
   * How long the breaks last. Held by the page rather than here because they
   * are edited from the itinerary once a route exists.
   */
  breakMinutes: { lunch: number; coffee: number };
  /**
   * Which breaks are switched on. Held by the page because a break can also
   * be removed from the itinerary once a route exists.
   */
  breaksEnabled: { lunch: boolean; coffee: boolean };
  /** A chosen place per break, or null to let the planner pick one. */
  breakPlaces: { lunch: Poi | null; coffee: Poi | null };
  onBreakPlaceChange: (kind: 'lunch' | 'coffee', poi: Poi | null) => void;
  /** Places removed from the itinerary, never to be suggested again. */
  excludePoiIds: string[];
  /**
   * Per-place and per-category avoidance radii. Anything absent uses the
   * slider's value, so only what was deliberately changed is carried.
   */
  /** Colour per category, shared with the map so the two always agree. */
  colourFor: (categoryKey: string) => string;
  avoidDistanceByPoiId: Record<string, number>;
  avoidDistanceByCategoryKey: Record<string, number>;
  /**
   * How each leg is travelled, set on the itinerary rather than here.
   *
   * Carried into the draft so that changing one re-plans exactly as any other
   * edit does — the form is still the only thing that builds a request.
   */
  legModes?: Record<string, TravelMode>;
  onAvoidDistanceOverride: (
    scope: 'poi' | 'category', key: string, metres: number | null,
  ) => void;
  onBreaksEnabledChange: (kind: 'lunch' | 'coffee', enabled: boolean) => void;
  /** Changes a break's length for this plan, overriding the configured default. */
  onBreakMinutesChange: (kind: 'lunch' | 'coffee', minutes: number) => void;
  /**
   * How long to stay at each spot added by hand, for this plan. The
   * configured default when the traveller has not chosen one.
   */
  defaultStopMinutes: number;
  /** Whether the safety overlay starts on, from configuration. */
  defaultSafetyPreference: boolean;
  onAvoidPinsChange: (pins: PinnedPoint[]) => void;
  /**
   * Reports the search corridor for the map to draw, or null to draw none.
   * Omit the prop and the toggle is not offered at all.
   */
  onCoverageChange?: (area: unknown | null) => void;
  /** Reports the city outlines for the map to draw, or null to draw none. */
  onRegionsChange?: (regions: unknown | null) => void;
  /**
   * The ground this journey covers, as the page worked it out.
   *
   * Used to narrow the city outlines to the ones the route actually crosses,
   * so the overlay and the counts describe the same cities.
   */
  corridorQuery?: CorridorQuery;
  /**
   * A trip built somewhere else, to open with.
   *
   * The assistant hands back the same PlanRequest this form builds, and the
   * page remounts the form with it — so what was asked for in words lands in
   * the fields, and can be edited by hand from there. Read only as initial
   * state: after that the form owns them, as it always did.
   */
  seed?: Partial<PlanRequest>;
  /** Interests, held by the page so both this and the assistant can set them. */
  interestKeys?: string[];
  /** Set from the form as well, now that the form has a control for them. */
  onInterestKeysChange?: (keys: string[]) => void;
  /**
   * The same categories, counted over the ground avoidance covers.
   *
   * Browsing covers whole cities the route crosses; avoidance covers the
   * roads and their reach and cannot cover more. Labelling an avoid chip
   * with the browsing number promised the plan would keep clear of 447
   * places when it would keep clear of 160.
   */
  avoidCategoryCounts?: Category[];
  /**
   * Opens a place's full record — photographs, hours, contact details.
   *
   * The role says what adding it from there would mean, so a cafe opened
   * from the lunch suggestions fills the break rather than becoming a stop.
   */
  onShowDetails: (poiId: string, role?: 'visit' | 'lunch' | 'coffee') => void;
  /**
   * Which suggested places the map should draw, and which is under the cursor.
   * Fires on every change, including back to empty.
   */
  onSuggestionPreview?: (preview: MapPreview) => void;
  /** Turns an external search result into a pin with the given role. */
  onExternalPick: (
    place: { name: string; lat: number; lng: number },
    role: 'start' | 'end' | 'visit' | 'avoid',
  ) => void;
}

/** A spot identified only by position. */
export interface PinnedPoint {
  lat: number;
  lng: number;
  label?: string;
  /** Street address, when the spot could be identified. */
  address?: string;
  /**
   * How long to stay here, when it has been given its own.
   *
   * On the pin rather than in the overrides map because a marked spot has no
   * id to key by: the map's keys are place ids and `pin-N` positions in a
   * finished route, and neither exists while the form is being filled in.
   */
  dwellMinutes?: number;
}

/** Re-exported from where it now lives, beside the fields that use it. */
export { BREAK_CATEGORIES } from './BreakFields';

/**
 * One break: whether it is happening, and — when it is — everything else,
 * which it shares with the per-day cards of a multi-day operation.
 *
 * The fields appear only once the break is switched on. Shown disabled they
 * take the same room while saying nothing, and the sidebar has none to spare.
 */
export function BreakRow({
  label, enabled, onEnabledChange, ...fields
}: {
  label: string;
  enabled: boolean;
  onEnabledChange: (on: boolean) => void;
} & Omit<React.ComponentProps<typeof BreakFields>, 'label'>) {
  return (
    <Box>
      <FormControlLabel
        control={
          <Switch checked={enabled} onChange={(event) => onEnabledChange(event.target.checked)} />
        }
        label={label}
      />
      {/*
        Indented under the switch, so the controls read as belonging to it.
      */}
      {enabled && (
        <Box sx={{ pl: 5.5 }}>
          <BreakFields label={label} {...fields} />
        </Box>
      )}
    </Box>
  );
}

/**
 * Today, and the time right now, as the API writes them.
 *
 * Local rather than UTC on purpose: a traveller in Washington planning at
 * eight in the evening means that evening, and `toISOString` would hand them
 * tomorrow. `HH:MM` to the minute — the planner has no use for seconds.
 */
function today(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function timeNow(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/**
 * Whether the one-day form offers a route style.
 *
 * Off for now — see the note at the control. One flag rather than a deleted
 * block, because the styles themselves are unchanged and this is a question
 * about where to ask, not whether to keep.
 */
const SHOW_STYLE_PICKER = false;

/*
 * The weighing numbers, hidden for now.
 *
 * They work: the budget refuses a detour it cannot afford, and the radius
 * changes which road counts as passing the most of what was asked for. But
 * they are five figures that mean nothing until somebody has moved one of
 * the sliders above them, and they were sitting in the ordinary run of the
 * form as though everyone had to answer them.
 *
 * Hidden rather than removed, and nothing else changes: whatever is already
 * set stays set, is still sent, and is still saved. An emphasis keeps its own
 * figures, and the site-wide settings still apply underneath. One line here
 * brings the controls back.
 */
const SHOW_PATH_NUMBERS = false;

export function PlannerForm({
  requestedMode,
  city, styles, submitting, onSubmit,
  mustVisit, orderHeldPoiIds, onMustVisitChange, onBrowsePlaces, avoid, onAvoidChange,
  avoidDistanceM, onAvoidDistanceChange,
  categories, avoidCategories, onAvoidCategoriesChange,
  availableMinutes, onAvailableMinutesChange,
  start, onStartChange, startPin, onStartPinChange,
  end, onEndChange, endPin, onEndPinChange,
  visitPins, onVisitPinsChange, avoidPins, onAvoidPinsChange, onExternalPick,
  viaPoints, pinnedOrder,
  onDraftChange, onReset, stopDwellMinutes, onStopDwellChange,
  breakMinutes, onBreakMinutesChange,
  breaksEnabled, onBreaksEnabledChange, excludePoiIds,
  colourFor, avoidDistanceByPoiId, avoidDistanceByCategoryKey, onAvoidDistanceOverride,
  legModes,
  breakPlaces, onBreakPlaceChange,
  defaultStopMinutes, defaultSafetyPreference,
  onShowDetails, onSuggestionPreview, onCoverageChange, onRegionsChange,
  avoidCategoryCounts, corridorQuery, seed, interestKeys = [],
  onInterestKeysChange,
}: Props) {
  const [styleKeys, setStyleKeys] = useState<string[]>(seed?.routeStyleKeys ?? []);
  /*
   * A theme, when one was asked for in words.
   *
   * The form offers no control for it — themes are chosen beside the
   * itinerary, as alternatives to a day already planned — but a request that
   * arrived with one must keep it, or the next edit would quietly drop the
   * whole shape of the day.
   */
  const [themeKey, setThemeKey] = useState<string | undefined>(seed?.themeKey);
  const [themes, setThemes] = useState<RouteTheme[]>([]);
  useEffect(() => {
    void api.themes().then(setThemes).catch(() => setThemes([]));
  }, []);
  /* A round trip: the route finishes where it began. */
  const [returnToStart, setReturnToStart] = useState(false);
  /* Open while asking what to do with a destination a round trip cannot use. */
  const [pendingRoundTrip, setPendingRoundTrip] = useState(false);
  const [mode, setMode] = useState<TravelMode>(seed?.mode ?? 'car');

  /*
   * A mode pressed on the failure alert rather than on the toggle here.
   *
   * When a route cannot be walked the planner offers the mode that does work,
   * and taking that offer has to move this control — otherwise the form still
   * reads "walk" while the map draws a drive. The counter is what lets the
   * same mode be asked for twice.
   */
  useEffect(() => {
    if (requestedMode) setMode(requestedMode.mode);
  }, [requestedMode]);

  /*
   * The browser's own position, usable as start or destination.
   *
   * Kept separate from the POI selections because it is a bare coordinate,
   * not a place in the database — the API takes either as a waypoint.
   */
  const [locating, setLocating] = useState<'start' | 'end' | null>(null);
  const [locateError, setLocateError] = useState<string | null>(null);
  const [startTime, setStartTime] = useState(seed?.startTime ?? '09:00');
  /*
   * Which day this is for. Required, and today until somebody says otherwise.
   *
   * It decides which weekday's opening hours are read — planning a Sunday on
   * a Wednesday would otherwise apply Wednesday's, and every stop's "open
   * when you arrive" would be answered against the wrong day. It was optional
   * once, with an empty box meaning today; that read as a field nobody had
   * to think about, which is the opposite of what it does.
   */
  const [date, setDate] = useState(seed?.date ?? today());
  /*
   * Leaving now, rather than at some hour of some day.
   *
   * Stamps both fields from the clock and holds them, because the two only
   * mean "now" together: today at 09:00 is not now at half past two. Turning
   * it off leaves the stamp behind to be edited, which is usually what
   * somebody wants next.
   */
  const [leavingNow, setLeavingNow] = useState(false);
  const lunch = breaksEnabled.lunch;
  const coffee = breaksEnabled.coffee;
  /*
   * Follows the configured default until the traveller touches it.
   *
   * Settings arrive after this mounts, so the initial value cannot simply be
   * read from a prop — and once someone has made a choice, a late-arriving
   * default must not overrule it.
   */
  /*
   * Only offered for modes that can meet a toll in the first place. Valhalla
   * accepts the option for car, taxi, motorcycle and truck; on foot or by bike
   * it would be a switch that does nothing.
   */
  /*
   * Read from the seed, never set here.
   *
   * The switch that set it has gone: tolls are answered in "How this one
   * travels", with the other road questions, on the same Auto / Avoid /
   * Prefer as the rest. This survives so a saved route or an emphasis that
   * carries `avoidTolls` still plans the way it was saved.
   */
  const avoidTolls = seed?.avoidTolls ?? false;
  /*
   * The road questions, answered for this journey rather than by an emphasis.
   *
   * Every one of them starts unanswered — "auto" — and an unanswered
   * question is not sent. The exception is `shortest`, which starts on and
   * has no control of its own, so it travels with every plan whether or not
   * this section was ever opened. See DEFAULT_ROAD_OPTIONS. An emphasis
   * answers the same questions for every round of its kind; these beat it
   * where both have spoken, because somebody saying "not today" about this
   * journey has said something more particular.
   */
  const [roadOptions, setRoadOptions] = useState<Record<string, number | boolean>>(
    (seed?.routingOptions as Record<string, number | boolean>)
      ?? { ...DEFAULT_ROAD_OPTIONS },
  );
  const [characteristics, setCharacteristics] = useState<Record<string, number>>(
    seed?.routeCharacteristics ?? {},
  );

  /*
   * What the chosen emphasis says, as form values.
   *
   * An emphasis used to answer these questions invisibly — the fields read
   * "Auto" while the server quietly applied the emphasis' own numbers, so the
   * one screen that could have shown what a plan would do showed nothing.
   * Choosing one now fills the form in, which makes the answer readable and
   * every part of it changeable for this journey alone.
   */
  const themeDefaults = useCallback((theme: RouteTheme | null) => ({
    road: { ...(theme?.routingOptions ?? {}) } as Record<string, number | boolean>,
    characteristics: { ...(theme?.routeCharacteristics ?? {}) } as Record<string, number>,
    path: Object.fromEntries(
      Object.entries(theme?.pathOptions ?? {})
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => [k, String(v)]),
    ) as Record<string, string>,
  }), []);
  /** Held as text, so a field can be emptied back to auto. */
  const [pathNumbers, setPathNumbers] = useState<Record<string, string>>(
    Object.fromEntries(Object.entries(seed?.pathOptions ?? {})
      .map(([key, value]) => [key, String(value)])),
  );

  /*
   * The emphasis actually chosen, and what it already answers.
   *
   * Three sections of this form ask questions an emphasis has answers to:
   * which sites to draw from, what to keep clear of, and how to travel. With
   * an emphasis chosen they were asked anyway, empty, beside a control that
   * had already answered them — so the form appeared to want an answer it
   * did not need, and an operator could not tell which of the two was in
   * force. Each of those sections now STATES what the emphasis says before
   * offering to override it.
   */
  const chosenTheme = themes.find((t) => t.key === themeKey) ?? null;

  /*
   * Whether the "name a place yourself" controls are open in the kinds mode.
   *
   * They belong there — choosing for me never meant choosing alone — but
   * shown unasked they sit between the kinds and the time budget with no
   * heading of their own, reading as a field somebody forgot to label. Asked
   * for, they explain themselves.
   *
   * The named mode has no toggle: naming places IS that mode.
   */
  const [alsoNaming, setAlsoNaming] = useState(false);

  /*
   * Whether each break is found for you or named by you.
   *
   * Opened on named where a place is already set — a seeded plan or one
   * handed over by the assistant can arrive with one — so the field holding
   * it is on screen rather than hidden behind a switch.
   */
  const [breakMode, setBreakMode] = useState<{ lunch: SitesMode; coffee: SitesMode }>(() => ({
    lunch: breakPlaces.lunch ? 'named' : 'kinds',
    coffee: breakPlaces.coffee ? 'named' : 'kinds',
  }));

  /*
   * An emphasis and "I'll name them" are two answers to one question.
   *
   * An emphasis fills the kinds AND keeps choosing by them: `themeKey` rides
   * on every request, so a round switched to the named mode would still come
   * back full of sites the emphasis picked, beside the handful that were
   * named. The mode would look obeyed and not be.
   *
   * So the switch asks first, rather than silently dropping something the
   * traveller chose. Held as the mode being moved to, which is also what
   * tells the dialog it is open.
   */

  // Shared with both multi-day forms, so the three cannot drift apart.
  const [sitesMode, setSitesMode] = useSitesMode(
    interestKeys, mustVisit.length + visitPins.length,
  );

  /*
   * Forced open, and the switch locked, once something has actually been
   * named: a stop that is on the round and sent with it must have a row on
   * screen to see and remove, whichever mode is showing.
   */
  const showNaming = sitesMode === 'named'
    || alsoNaming
    || mustVisit.length > 0
    || visitPins.length > 0;

  /*
   * Whether a time budget decides anything on this round.
   *
   * It rations the stops the PLANNER chooses: the emphasis' own kinds, and
   * any kind added here. With no kinds there is nothing for it to choose
   * between, so the round is whatever was named plus the road between — a
   * budget rations nothing, and asking for one invites the reading that a
   * smaller number produces a shorter journey.
   *
   * Breaks and named places were in this test and are not any more. A lunch
   * stop is on by default, so counting it meant the field appeared on an
   * untouched form with nothing to ration; a named place is a place already
   * decided, and the field only shows in the kinds mode regardless.
   *
   * The value is kept rather than cleared while it is away, so adding a kind
   * back brings the same number back with it.
   */
  const budgetDecides =
    interestKeys.length > 0
    || (chosenTheme?.categoryKeys.length ?? 0) > 0;


  /*
   * Filled when the emphasis changes, not on every render: these fields are
   * the traveller's to edit afterwards, and re-applying them would undo the
   * edit as fast as it was made. Clearing the emphasis empties them again,
   * because what is on screen should be what will be sent.
   */
  const appliedTheme = useRef<string | undefined>(undefined);
  /*
   * What the answer this form opened on had already stated.
   *
   * An emphasis fills what nobody has answered; it does not overrule what was
   * asked for. These come from the assistant, and "keep off the main roads"
   * is the operator's instruction whether it arrived through this dropdown or
   * through a sentence — so the emphasis being applied for the first time
   * must not write over it the instant the form remounts around the answer.
   *
   * Only the first fill is protected. After that these are simply the values
   * on screen, and changing emphasis deliberately is allowed to move them.
   */
  const statedInSeed = useRef({
    road: new Set(Object.keys(seed?.routingOptions ?? {})),
    characteristics: new Set(Object.keys(seed?.routeCharacteristics ?? {})),
    path: new Set(Object.keys(seed?.pathOptions ?? {})),
  });
  const honourSeed = useRef(true);
  // What the emphasis currently in force put into the form, so it can be
  // taken back out again when that emphasis is cleared or replaced.
  const appliedValues = useRef<ReturnType<typeof themeDefaults> | null>(null);
  // The category lists the emphasis in force contributed, for the same reason.
  const previousLists = useRef<{ draws: string[]; avoids: string[]; boosts: string[] }>(
    { draws: [], avoids: [], boosts: [] },
  );
  useEffect(() => {
    if (appliedTheme.current === themeKey) return;
    /*
     * Not until the emphases have arrived.
     *
     * They are fetched, so on a fresh mount this list is empty for a moment —
     * and an emphasis looked up in an empty list answers "no categories, no
     * road preferences, nothing". Marking it applied on that reading recorded
     * that the fill had happened when it had filled nothing, and the guard
     * above then turned away the run that had the real answer.
     *
     * Picking one from the menu was unaffected, because by then the list is
     * loaded. What broke was every path that arrives with an emphasis ALREADY
     * chosen — the form remounted around an assistant's answer, most of all,
     * where the emphasis showed in its box and the two lists it is mostly
     * made of stayed empty beside it.
     */
    if (themeKey && themes.length === 0) return;
    appliedTheme.current = themeKey;

    /*
     * Only the questions the emphasis answers.
     *
     * Merged over what is already there rather than replacing it: an emphasis
     * that says nothing about ferries has not said "let the router decide",
     * it has said nothing — and overwriting an answer somebody gave by hand
     * with Auto would be putting words in their mouth. Clearing the emphasis
     * leaves the fields as they stand, because by then the values on screen
     * are what will be sent whether an emphasis is named or not.
     */
    const previous = appliedValues.current;
    const next = themeDefaults(themes.find((t) => t.key === themeKey) ?? null);
    appliedValues.current = next;

    /*
     * Out with the old emphasis' answers, in with the new one's.
     *
     * Only the values still standing as that emphasis left them are taken
     * back: one changed by hand has stopped being the emphasis' answer and
     * become the traveller's, and clearing an emphasis is not a reason to
     * discard what somebody typed. Everything the emphasis never spoke about
     * is untouched throughout.
     */
    const swap = <T,>(
      current: Record<string, T>,
      was: Record<string, T>,
      now: Record<string, T>,
      stated?: Set<string>,
    ) => {
      const out = { ...current };
      for (const [key, value] of Object.entries(was)) {
        if (out[key] === value) delete out[key];
      }

      const filled = { ...out };
      for (const [key, value] of Object.entries(now)) {
        // Whatever the answer already settled stays settled.
        if (stated?.has(key) && key in out) continue;
        filled[key] = value;
      }

      return filled;
    };

    const stated = honourSeed.current ? statedInSeed.current : null;
    honourSeed.current = false;

    setRoadOptions((current) => withRoadDefaults(
      swap(current, previous?.road ?? {}, next.road, stated?.road)));
    setCharacteristics((current) =>
      swap(current, previous?.characteristics ?? {}, next.characteristics,
        stated?.characteristics));
    setPathNumbers((current) =>
      swap(current, previous?.path ?? {}, next.path, stated?.path));

    /*
     * The kinds of site, and what to keep clear of, on the same terms.
     *
     * An emphasis is mostly these two lists — what it collects and what it
     * excludes — and they were the part still applied invisibly on the server
     * while the form showed an empty box. Added to rather than replacing, and
     * taken back the same way when the emphasis is cleared, so a category
     * somebody chose by hand survives either.
     */
    const chosen = themes.find((t) => t.key === themeKey) ?? null;
    const wasDraws = previousLists.current.draws;
    const wasAvoids = previousLists.current.avoids;
    const wasBoosts = previousLists.current.boosts;
    const nowDraws = chosen?.categoryKeys ?? [];
    const nowAvoids = chosen?.avoidCategoryKeys ?? [];
    const nowBoosts = chosen?.boostCategoryKeys ?? [];
    previousLists.current = { draws: nowDraws, avoids: nowAvoids, boosts: nowBoosts };

    const merge = (current: string[], was: string[], now: string[]) => {
      const kept = current.filter((key) => !was.includes(key));

      return [...new Set([...kept, ...now])];
    };

    if (wasDraws.length > 0 || nowDraws.length > 0) {
      onInterestKeysChange?.(merge(interestKeys, wasDraws, nowDraws));
    }
    if (wasAvoids.length > 0 || nowAvoids.length > 0) {
      onAvoidCategoriesChange(merge(avoidCategories, wasAvoids, nowAvoids));
    }
    /*
     * What it favours among what it draws from — the third list an emphasis
     * carries, and the one that was still being applied invisibly. Set from
     * the state itself rather than a captured copy, so it does not depend on
     * where this sits relative to the declaration.
     */
    if (wasBoosts.length > 0 || nowBoosts.length > 0) {
      setBoostKeys((current) => merge(current, wasBoosts, nowBoosts));
    }
    // The lists are read, not watched: re-running on every edit to them would
    // fight the traveller for the box they are typing in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeKey, themes, themeDefaults]);

  /*
   * Whether anything has been moved away from what the emphasis says. Compared
   * by value rather than tracked by a flag, so putting a field back by hand
   * counts as unchanged — which is what somebody who did exactly that means.
   */
  const themeEdited = useMemo(() => {
    if (!chosenTheme) return false;
    const base = themeDefaults(chosenTheme);

    /*
     * Only the questions the emphasis answered are compared. A control it
     * never spoke about is nobody's default, so changing one is not a
     * departure from the emphasis and must not offer to be undone.
     */
    const differs = (
      want: Record<string, unknown>,
      have: Record<string, unknown>,
    ) => Object.entries(want).some(([key, value]) => have[key] !== value);

    return differs(base.road, roadOptions)
      || differs(base.characteristics, characteristics)
      || differs(base.path, pathNumbers);
  }, [chosenTheme, roadOptions, characteristics, pathNumbers, themeDefaults]);

  // Puts back only what the emphasis says, for the same reason.
  const restoreTheme = useCallback(() => {
    const next = themeDefaults(chosenTheme);
    setRoadOptions((current) => ({ ...current, ...next.road }));
    setCharacteristics((current) => ({ ...current, ...next.characteristics }));
    setPathNumbers((current) => ({ ...current, ...next.path }));
  }, [chosenTheme, themeDefaults]);
  const labelFor = (key: string) =>
    categories.find((c) => c.key === key)?.label ?? key.replace(/_/g, ' ');


  /*
   * How many of those questions have actually been answered.
   *
   * On the shut section's own row, because a section that is shut by design
   * has to say when it is no longer empty — otherwise a preference set an
   * hour ago goes on quietly shaping every re-plan out of sight.
   */
  const roadAnswers = countRoadAnswers(roadOptions)
    + Object.keys(characteristics).length
    + Object.values(pathNumbers).filter((value) => value.trim() !== '').length;
  const tollsApply = ['car', 'taxi', 'motorcycle', 'truck'].includes(mode);

  /*
   * Read from Admin, never set here.
   *
   * The switch that set it has gone: "Show infrastructure proximity by
   * default" in Admin decides it now, for every round, which is where a
   * question about what this deployment reports belongs.
   */
  const safetyPreference = defaultSafetyPreference;
  /*
   * A seeded break arrives as minutes from the start; this form thinks in
   * clock times, so it is converted once, here, rather than in two places.
   */
  const breakClock = (minutesFromStart: number | undefined, fallback: string) => {
    if (minutesFromStart == null) return fallback;
    const base = seed?.startTime ?? '09:00';
    const [hours, minutes] = base.split(':').map(Number);
    const total = (hours ?? 9) * 60 + (minutes ?? 0) + minutesFromStart;
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:`
      + `${String(total % 60).padStart(2, '0')}`;
  };
  const [lunchAt, setLunchAt] = useState(
    breakClock(seed?.lunch?.targetMinutesFromStart, '13:00'),
  );
  const [coffeeAt, setCoffeeAt] = useState(
    breakClock(seed?.coffee?.targetMinutesFromStart, '16:00'),
  );

  /*
   * Whether each break is pinned to an hour, or left to the planner.
   *
   * Loose by default. A time window is a hard constraint — miss it and the
   * break is dropped — so a round arrived with 1:00 and 4:00 already set,
   * neither of them asked for, and the commonest reason a break never
   * appeared was a window nobody chose. Most people want lunch somewhere in
   * the middle; the ones who need it at one o'clock can say so.
   *
   * A round arriving with its own times — a saved route, or the assistant —
   * opens pinned, because those were chosen.
   */
  const [lunchPinned, setLunchPinned] = useState(Boolean(seed?.lunch?.targetMinutesFromStart));
  const [coffeePinned, setCoffeePinned] = useState(Boolean(seed?.coffee?.targetMinutesFromStart));
  /*
   * Which of the chosen kinds to favour.
   *
   * Held by the form rather than the page: it is a preference among what is
   * on screen, and it means nothing once those categories are gone — which
   * is exactly when the form is remounted anyway.
   */
  const [boostKeys, setBoostKeys] = useState<string[]>(seed?.boostCategoryKeys ?? []);
  /** Stay times per kind of site, for this round only. */
  const [categoryDwell, setCategoryDwell] = useState<Record<string, number>>(
    seed?.dwellMinutesByCategoryKey ?? {},
  );
  // The exclusion filter still only narrows the search beneath it.
  const [avoidFilter, setAvoidFilter] = useState('');

  const selectedStyles = useMemo(
    () => styleKeys
      .map((key) => styles.find((s) => s.key === key))
      .filter((s): s is RouteStyle => Boolean(s)),
    [styles, styleKeys],
  );

  /*
   * The first style settles anything that cannot be combined.
   *
   * Categories from every chosen style are pooled by the server, but pacing
   * and departure time have no sensible average, so they follow whichever was
   * picked first - the same rule the server applies.
   */
  const selectedStyle = useMemo(
    () => selectedStyles[0] ?? null,
    [selectedStyles],
  );

  /*
   * Picking a style pre-fills the travel mode and departure time it implies.
   *
   * It deliberately does NOT flip the break or safety switches. Silently
   * ticking a checkbox the traveller never touched is surprising, and they
   * then have to notice and undo it. The style's own suggestions still shape
   * which places are proposed; the switches stay theirs.
   */
  useEffect(() => {
    if (!selectedStyle) return;
    setMode(selectedStyle.defaultMode);
    if (selectedStyle.timeOfDay === 'morning') setStartTime('09:00');
    else if (selectedStyle.timeOfDay === 'afternoon') setStartTime('13:00');
    else if (selectedStyle.timeOfDay === 'evening') setStartTime('18:00');
  }, [selectedStyle]);

  /**
   * Asks the browser where we are, then checks it against the city bounds —
   * planning a Washington route from Berlin would otherwise fail deep inside
   * the routing engine with nothing useful to show.
   */
  const useCurrentLocation = (which: 'start' | 'end') => {
    if (!navigator.geolocation) {
      setLocateError('This browser cannot report your location.');
      return;
    }
    setLocating(which);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        const [minLng, minLat, maxLng, maxLat] = city.bbox;
        setLocating(null);

        if (lng < minLng || lng > maxLng || lat < minLat || lat > maxLat) {
          setLocateError(`You appear to be outside ${city.name}, so it cannot be used here.`);
          return;
        }
        if (which === 'start') { onStartPinChange({ lat, lng, label: 'My location' }); onStartChange(null); }
        else { onEndPinChange({ lat, lng, label: 'My location' }); onEndChange(null); }
      },
      (err) => {
        setLocating(null);
        setLocateError(
          err.code === err.PERMISSION_DENIED
            ? 'Location permission was denied.'
            : 'Could not get your location.',
        );
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  const transitUnavailable = mode === 'transit' && !city.otpSlot;

  const minutesFromStart = (clock: string): number => {
    const [sh = 0, sm = 0] = startTime.split(':').map(Number);
    const [th = 0, tm = 0] = clock.split(':').map(Number);
    return Math.max(0, th * 60 + tm - (sh * 60 + sm));
  };

  /*
   * The request the form currently describes.
   *
   * Built continuously rather than only on submit, so that once a route
   * exists the page can re-plan from it the moment anything changes. Null
   * until both ends are set, which is the point before which there is
   * nothing to route.
   */
  const draft = useMemo<PlanRequest | null>(() => {
    if (!(start || startPin)) return null;
    // Nothing to plan against without a day: opening hours are read by weekday.
    if (!date) return null;
    // A round trip needs only a start; anything else needs both ends.
    if (!returnToStart && !(end || endPin)) return null;

    const startRef: PlanRequest['start'] = startPin
      ? {
          kind: 'coords',
          lat: startPin.lat,
          lng: startPin.lng,
          label: startPin.label ?? 'Pinned start',
        }
      : { kind: 'poi', poiId: start!.id };

    return {
      cityId: city.id,
      start: startRef,
      // The same place, sent as the end. The router treats it as any other
      // destination, so a round trip needs no special handling downstream.
      end: returnToStart
        ? startRef
        : endPin
          ? {
              kind: 'coords',
              lat: endPin.lat,
              lng: endPin.lng,
              label: endPin.label ?? 'Pinned destination',
            }
          : { kind: 'poi', poiId: end!.id },
      mode,
      startTime,
      date,
      availableMinutes,
      ...(styleKeys.length > 0 ? { routeStyleKeys: styleKeys } : {}),
      mustVisitPoiIds: [...mustVisit.map((p) => p.id), ...(orderHeldPoiIds ?? [])],
      // The chosen stay rides along on each pinned stop; a stay set against
              // an individual stop is applied later and still wins.
      mustVisitPoints: visitPins.map((pin) => ({
        ...pin,
        dwellMinutes: pin.dwellMinutes ?? defaultStopMinutes,
      })),
      viaPoints,
      ...(pinnedOrder ? { stopOrderLocked: true, stopOrder: pinnedOrder } : {}),
      stopDwellMinutes,
      // Sent only once a leg has actually been changed, so an untouched day
      // carries no overrides and plans exactly as it always did.
      ...(legModes && Object.keys(legModes).length > 0 ? { legModes } : {}),
      /*
       * Kinds are sent only while the round is being built FROM kinds.
       *
       * Interests belong to the page, because the assistant sets them too.
       * Leaving them on the request in the named mode would fill the day with
       * places nobody picked, beside the handful that were — which is the
       * behaviour the mode switch exists to separate. The values are kept,
       * not cleared, so switching back sends them again unchanged.
       */
      interestCategoryKeys: sitesMode === 'kinds' ? interestKeys : [],
      // Only what is still chosen: a favourite left over from a category
      // since removed would tilt the ranking towards nothing.
      boostCategoryKeys: sitesMode === 'kinds'
        ? boostKeys.filter((key) => interestKeys.includes(key))
        : [],
      dwellMinutesByCategoryKey: categoryDwell,
      /*
        Named mode holds exactly the places named, and no more.

        With no kinds of its own the planner falls back to the emphasis's,
        which is right in the other mode and wrong here — it is how an
        emphasis went on adding sites to a round whose whole point was that
        the traveller chose them. A ceiling the named places already fill
        leaves nothing for a suggestion to occupy, so the emphasis keeps
        shaping the ROUTE without choosing what is on it.
      */
      ...(sitesMode === 'named'
        ? { maxStops: mustVisit.length + visitPins.length + (orderHeldPoiIds?.length ?? 0) }
        : {}),
      ...(themeKey ? { themeKey } : {}),
      excludePoiIds,
      avoid: {
        poiIds: avoid.map((p) => p.id),
        categoryKeys: avoidCategories,
        points: avoidPins,
        distanceByPoiId: avoidDistanceByPoiId,
        distanceByCategoryKey: avoidDistanceByCategoryKey,
        ...(avoid.length > 0 || avoidCategories.length > 0 || avoidPins.length > 0
          ? { distanceM: avoidDistanceM } : {}),
      },
      safetyPreference: safetyPreference ? 'prefer' : 'off',
      // Sent only where it means something, so a walking route never carries
      // a costing option Valhalla would ignore.
      ...(tollsApply && avoidTolls ? { avoidTolls: true } : {}),
      /*
       * Only what was answered. An empty object would be a request saying
       * "no preferences" where the truth is "nobody was asked", and the
       * emphasis's own preferences would then have nothing to merge with.
       */
      ...(Object.keys(roadOptions).length > 0 ? { routingOptions: roadOptions } : {}),
      ...(Object.keys(characteristics).length > 0
        ? { routeCharacteristics: characteristics } : {}),
      ...(Object.keys(pathNumbers).some((key) => pathNumbers[key]!.trim() !== '')
        ? {
          pathOptions: Object.fromEntries(
            Object.entries(pathNumbers)
              .filter(([, value]) => value.trim() !== '')
              .map(([key, value]) => [key, Number(value)]),
          ),
        }
        : {}),
      ...(lunch
        ? {
            lunch: {
              targetMinutesFromStart: minutesFromStart(lunchAt),
              ...(lunchPinned ? {} : { anytime: true }),
              durationMinutes: breakMinutes.lunch,
              flexibilityMinutes: 60,
              ...(breakMode.lunch === 'named' && breakPlaces.lunch
                ? { poiId: breakPlaces.lunch.id } : {}),
            },
          }
        : {}),
      ...(coffee
        ? {
            coffee: {
              targetMinutesFromStart: minutesFromStart(coffeeAt),
              ...(coffeePinned ? {} : { anytime: true }),
              durationMinutes: breakMinutes.coffee,
              flexibilityMinutes: 60,
              ...(breakMode.coffee === 'named' && breakPlaces.coffee
                ? { poiId: breakPlaces.coffee.id } : {}),
            },
          }
        : {}),
    };
    // minutesFromStart is derived from startTime, which is already a
    // dependency; listing the function itself would recreate the draft on
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    city.id, start, startPin, end, endPin, returnToStart, mode, startTime, date,
    availableMinutes,
    styleKeys,
    mustVisit, orderHeldPoiIds, visitPins, avoid, avoidCategories, avoidPins,
    avoidDistanceM, viaPoints,
    pinnedOrder, safetyPreference, avoidTolls, tollsApply, lunch, lunchAt, coffee, coffeeAt,
    roadOptions, characteristics, pathNumbers,
    stopDwellMinutes, breakMinutes, defaultStopMinutes, excludePoiIds, breakPlaces,
    avoidDistanceByPoiId, avoidDistanceByCategoryKey, legModes, interestKeys, boostKeys,
    categoryDwell, themeKey, sitesMode, breakMode,
  ]);

  // Reported to the page so an existing route can follow the form live.
  useEffect(() => { onDraftChange?.(draft); }, [draft, onDraftChange]);

  /*
   * One request behind every suggestion on this form.
   *
   * Null until both ends are set, which is also the point before which
   * "along the way" means nothing — so the panel and the break lists simply
   * do not offer to look.
   */
  const recommendations = useRecommendations(draft);

  /*
   * The corridor, fetched only while somebody is looking at it.
   *
   * Re-fetched whenever the request changes, because the radius is derived
   * from the mode and the time available — a shape drawn for a five-hour
   * drive is not the shape of a two-hour walk, and leaving the old one up
   * would be worse than showing nothing.
   */
  const [showCoverage, setShowCoverage] = useState(false);
  const [showRegions, setShowRegions] = useState(false);

  useEffect(() => {
    if (!onRegionsChange) return;
    if (!showRegions) { onRegionsChange(null); return; }
    let cancelled = false;
    // Narrowed to the cities this route crosses, which is what the counts
    // cover — every outline at once answers a question nobody asked.
    void api.cityRegions(corridorQuery)
      .then((r) => { if (!cancelled) onRegionsChange(r); })
      .catch(() => { if (!cancelled) onRegionsChange(null); });
    return () => { cancelled = true; };
  }, [showRegions, onRegionsChange, corridorQuery]);

  useEffect(() => {
    if (!showCoverage || !draft) {
      onCoverageChange?.(null);
      return;
    }
    let cancelled = false;
    void api.coverage(draft)
      .then((r) => { if (!cancelled) onCoverageChange?.(r.area); })
      .catch(() => { if (!cancelled) onCoverageChange?.(null); });
    return () => { cancelled = true; };
  }, [showCoverage, draft, onCoverageChange]);

  /*
   * Reported up so the map can draw what the list is offering.
   *
   * The state lives here, with the lists that produce it, rather than in the
   * page — but the map is the page's. This is the same shape as the draft
   * loop above and for the same reason.
   */
  useEffect(() => {
    onSuggestionPreview?.(recommendations.preview);
  }, [recommendations.preview, onSuggestionPreview]);

  // The draft is null until the form describes something routable, which is
  // exactly the condition for the button being usable.
  const canSubmit = Boolean(draft) && !submitting && !transitUnavailable;

  /*
    One place cannot be two things on the same round.

    Checked here, before the request goes, because the planner's answer to a
    stop that is also an end is a second arrival at the same coordinates — a
    round nobody asked for, drawn after a wait, with nothing saying why. The
    pickers already refuse the case they can see; this catches the rest,
    including a spot marked on top of a place already on the round, where no
    id matches and only the distance does.

    A round trip is exempt from the start/end rule by design: finishing where
    it began is what that switch MEANS, and the planner reads it the same way
    — from the two ends being within fifty metres of each other.
  */
  const placeProblems = samePlaceProblems({
    start: start
      ? { lat: start.lat, lng: start.lng, id: start.id, label: start.name }
      : startPin
        ? { lat: startPin.lat, lng: startPin.lng, label: startPin.label ?? 'the marked start' }
        : null,
    end: end
      ? { lat: end.lat, lng: end.lng, id: end.id, label: end.name }
      : endPin
        ? { lat: endPin.lat, lng: endPin.lng, label: endPin.label ?? 'the marked destination' }
        : null,
    stops: [
      ...mustVisit.map((p) => ({ lat: p.lat, lng: p.lng, id: p.id, label: p.name })),
      ...visitPins.map((p) => ({
        lat: p.lat, lng: p.lng, label: p.label ?? 'a marked stop',
      })),
    ],
    roundTrip: returnToStart,
  });

  const submit = () => { if (draft && placeProblems.length === 0) onSubmit(draft); };

  return (
    <Stack spacing={2.5}>
      {/* ── style ────────────────────────────────────────────────── */}
      {/*
        Hidden on the single-day form for now.

        A style and a theme answer the same question in different words, and
        the themed alternatives beside the itinerary are the better place to
        ask it: they show what each one does to the day rather than naming a
        preset before anything exists. The trip form still offers it — a
        week needs a shape stated once rather than compared seven times — and
        the request still carries `routeStyleKeys`, so a saved route or the
        assistant can set one and this form will keep it.
      */}
      {SHOW_STYLE_PICKER && (
      <Autocomplete
        size="small"
        fullWidth
        options={styles}
        value={selectedStyle ?? null}
        onChange={(_, next) => setStyleKeys(next ? [next.key] : [])}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(a, b) => a.key === b.key}
        slotProps={{ listbox: { sx: { maxHeight: 320 } } }}
        renderOption={(props, option) => {
          const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
          return (
            <Box component="li" key={key} {...rest}>
              <Box component="span" sx={{ mr: 1 }}>{option.icon}</Box>
              <Typography variant="body2" noWrap>{option.label}</Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Route style"
            placeholder={selectedStyle ? undefined : 'Any style'}
            helperText={
              selectedStyle?.description
              ?? 'Optional — shapes what gets suggested'
            }
          />
        )}
      />
      )}

      {/*
        An emphasis, chosen here rather than only offered once a route exists.

        It is the control that says what the day is FOR, now that styles are
        not offered on this form: it reserves a share of the stops before the
        route is worked out, and carries what to keep clear of with it.
        Offering it only as an alternative to a finished plan meant the first
        plan could never be asked for.
      */}
      {/*
        Above the field, not under it.

        As guidance it belongs where somebody reads before deciding; the slot
        below now carries the chosen emphasis' own description, which answers
        the same question about the specific one picked.
      */}
      <Typography data-tour="theme" variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        Choose a theme to automatically set the types of sites to visit and areas to avoid.
      </Typography>
      <Autocomplete
        data-tour="theme"
        size="small"
        fullWidth
        options={themes.map((t) => t.key)}
        value={themeKey ?? null}
        onChange={(_, next) => setThemeKey(next ?? undefined)}
        getOptionLabel={(key) => {
          const theme = themes.find((t) => t.key === key);
          return theme ? themeName(theme.key, theme.label) : key;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Mission emphasis"
            placeholder="Optional"
            /*
              What picking one DOES, not what it does internally.

              It read "Reserves a share of the stops for its own kinds of
              site, and keeps the route clear of what it excludes" — true,
              and a description of the planner's working. Somebody deciding
              whether to touch this field wants to know it will fill in the
              two lists below for them.

              A chosen emphasis replaces this with its own description, which
              is the more specific answer to the same question.
            */
            helperText={
              themes.find((t) => t.key === themeKey)?.description
              ?? undefined
            }
          />
        )}
      />

      {/* ── travel mode ──────────────────────────────────────────── */}
      <Box data-tour="transport">
        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
          Transport
        </Typography>
        {/*
          One row, filling the width, as the multi-day form draws it.

          These were natural-width buttons allowed to wrap, so the row ended
          short of the fields above and below it and a narrow panel could
          drop the last mode onto a line of its own, where it read as a
          different control. Stretched evenly they line up with everything
          else and cannot wrap at all.

          The tooltip goes INSIDE the button rather than around it: wrapped,
          its span sat between the group and the button and took the flex
          sizing for itself.
        */}
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, next: TravelMode | null) => next && setMode(next)}
          sx={{ display: 'flex' }}
        >
          {SELECTABLE_TRAVEL_MODES.map((m) => (
            <ToggleButton key={m} value={m} sx={{ flex: 1, minWidth: 0, px: 0.5 }}>
              <Tooltip title={MODE_LABELS[m]} arrow>
                <Box sx={{ display: 'flex' }}>{MODE_ICONS[m]}</Box>
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {transitUnavailable && (
        <Alert severity="info" variant="outlined">
          This city has no transit graph. Import GTFS feeds in Admin to enable it.
        </Alert>
      )}

      {/* ── endpoints ────────────────────────────────────────────── */}
      <Endpoint
        label="Starting at"
        placeholder="Where are you starting?"
        cityId={city.id}
        value={start}
        onChange={onStartChange}
        here={startPin}
        onClearHere={() => onStartPinChange(null)}
        onUseHere={() => useCurrentLocation('start')}
        onExternalPick={(place) => onExternalPick(place, 'start')}
        locating={locating === 'start'}
      />
      {/*
        A round trip has no separate destination, so the field is hidden
        rather than shown holding a copy of the start — two fields that must
        agree are two fields that can disagree.
      */}
      <FormControlLabel
        control={
          <Switch
            checked={returnToStart}
            onChange={(event) => {
              /*
               * Turning this on with a destination already chosen would
               * quietly discard it — the field hides and the plan ends at the
               * start. Better to ask what the place was meant to be: somewhere
               * to call at on the way, or a mistake.
               */
              if (event.target.checked && (end || endPin)) {
                setPendingRoundTrip(true);
                return;
              }
              setReturnToStart(event.target.checked);
            }}
          />
        }
        label="Return to the starting point"
        sx={{ mt: -1 }}
      />

      <Dialog
        open={pendingRoundTrip}
        onClose={() => setPendingRoundTrip(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0.5 }}>Keep the destination?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            A round trip ends where it starts, so{' '}
            <strong>{end?.name ?? endPin?.label ?? 'your destination'}</strong>{' '}
            cannot be the end of it. It can still be somewhere you call at along
            the way.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
          <Button onClick={() => setPendingRoundTrip(false)}>Cancel</Button>
          <Box sx={{ flex: 1 }} />
          <Button
            color="inherit"
            onClick={() => {
              onEndChange(null);
              onEndPinChange(null);
              setReturnToStart(true);
              setPendingRoundTrip(false);
            }}
          >
            Remove it
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // Kept as a stop, in whichever list matches what it is: a known
              // place goes to the places list, a dropped pin to the pins.
              if (end) onMustVisitChange([...mustVisit, end]);
              else if (endPin) onVisitPinsChange([...visitPins, endPin]);
              onEndChange(null);
              onEndPinChange(null);
              setReturnToStart(true);
              setPendingRoundTrip(false);
            }}
          >
            Make it a stop
          </Button>
        </DialogActions>
      </Dialog>

      {!returnToStart && (
        <Endpoint
          label="Destination"
          placeholder="Where do you need to end up?"
          cityId={city.id}
          value={end}
          onChange={onEndChange}
          here={endPin}
          onClearHere={() => onEndPinChange(null)}
          onUseHere={() => useCurrentLocation('end')}
          onExternalPick={(place) => onExternalPick(place, 'end')}
          locating={locating === 'end'}
        />
      )}
      {locateError && (
        <Alert severity="warning" variant="outlined" onClose={() => setLocateError(null)}>
          {locateError}
        </Alert>
      )}

      {/*
        The catalogue, beside the two ends as well as beside the stops.

        The pickers above want a name typed at them. Choosing where a round
        starts and finishes is exactly when somebody does not have one —
        they are looking for a place that suits, and "which of these has been
        worked already?" is part of what suits. Same window, same three
        things it can set.
      */}
      {onBrowsePlaces && (
        <Button
          size="small"
          startIcon={<TravelExploreIcon />}
          onClick={onBrowsePlaces}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          Browse places for the start or destination
        </Button>
      )}

      {/*
        ── time ───────────────────────────────────────────────────
        Two rows rather than one wrapping row. The hour, the day and the
        toggle that fills both in belong together and fit across the sidebar;
        "Time available" does not fit beside them and was losing its own label
        when squeezed, so it gets the width to itself.
      */}
      <Stack spacing={2} data-tour="time">
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap', rowGap: 1.5, alignItems: 'center' }}>
          <TextField
            label="Start time"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            disabled={leavingNow}
            sx={{ flex: '1 1 130px', minWidth: 130 }}
          />
          {/*
            Required. It decides which weekday's opening hours every stop is
            read against — planning next Sunday on a Wednesday would otherwise
            close the wrong places — so there is no sensible blank for it.
          */}
          <TextField
            label="Date"
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={leavingNow}
            error={!date}
            helperText={date ? '' : 'Pick the day you are going'}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ flex: '1 1 155px', minWidth: 155 }}
          />
          {/*
            Both fields at once, from the clock.

            Kept as a toggle rather than a button so the state is legible: while
            it is on, the two fields show what "now" currently is and are not
            somebody's stale typing. Re-stamped on the way on, since a form left
            open for an hour would otherwise still be leaving an hour ago.
          */}
          <FormControlLabel
            sx={{ m: 0, flex: '0 0 auto' }}
            control={
              <Switch
                size="small"
                checked={leavingNow}
                onChange={(e) => {
                  setLeavingNow(e.target.checked);
                  if (e.target.checked) { setDate(today()); setStartTime(timeNow()); }
                }}
              />
            }
            label={<Typography variant="body2">Leaving now</Typography>}
          />
        </Stack>
      </Stack>

      {/*
        What the search will actually cover, on request.
        Offered only once there is a journey to cover — before both ends and a
        length are set there is no corridor, and a radius drawn from half a
        request would be a guess presented as a fact.
      */}
      {FEATURES.mapDiagnostics && draft && onCoverageChange && (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showCoverage}
              onChange={(e) => setShowCoverage(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              Show where sites are drawn from
              <Typography component="span" variant="caption" color="text.secondary">
                {' '}— as far as your time and travel mode reach
              </Typography>
            </Typography>
          }
        />
      )}

      {/*
        Which ground each city covers, on request.
        
        Worth having beside the corridor toggle rather than buried in Admin: a
        "city" can be a whole state now, so a start that looks perfectly
        central can be two states outside the city being planned — and the
        only symptom is a plan that comes back with nothing in it.
      */}
      {FEATURES.mapDiagnostics && onRegionsChange && (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showRegions}
              onChange={(e) => setShowRegions(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              Show area boundaries
            </Typography>
          }
        />
      )}

      {/*
        The line naming the cities this journey crosses has gone.

        It said "Within District of Columbia — counts and places below cover
        it whole", which on a round inside one city is a fact nobody needed:
        the city is named at the top of the form already, and the sentence
        sat between the dates and the sites explaining a subtlety about the
        counts that the counts themselves never raised. On a round that
        crosses cities the subtlety is real, but it belongs beside a count
        that surprises somebody, not above all of them.
      */}

      <Divider />

      {/* ── places ───────────────────────────────────────────────────
        Two near-identical controls sit here — a category filter and a place
        picker, twice over. Without headings and a boundary the second pair
        reads as a repeat of the first, and nothing says which list a field
        feeds. Each group is therefore titled and boxed.
      */}
      <FieldGroup
        tour="visit"
        title="Sites to visit"
        caption="What should this round actually stop at?"
        count={interestKeys.length + mustVisit.length + visitPins.length}
        defaultExpanded={interestKeys.length > 0 || mustVisit.length > 0}
      >
        {/*
          The catalogue, before anything has been named.

          The pickers below answer "which place did you mean?" and need a name
          to start from. Somebody building a round often has the other question
          — of the places that would do, which has already been worked, and
          by whom? — which needs a list to browse, the coverage record beside
          it, and the three things a place can be to a round. First in the
          section because it is where a round with nothing in it starts.
        */}
        {onBrowsePlaces && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<TravelExploreIcon />}
            onClick={onBrowsePlaces}
            sx={{ mb: 1.5, textTransform: 'none' }}
          >
            Browse places, with their coverage
          </Button>
        )}

        {/*
          What the emphasis already draws from, stated rather than implied.
          Added to, not replaced: a kind picked here is searched alongside the
          emphasis's own. No share is quoted — nothing reserves one.
        */}
        {chosenTheme && chosenTheme.categoryKeys.length > 0 && (
          <Alert severity="info" variant="outlined" sx={{ mb: 1.5, py: 0.5 }}>
            <Typography variant="caption">
              {themeName(chosenTheme.key, chosenTheme.label)} already draws from{' '}
              {chosenTheme.categoryKeys.map(labelFor).join(', ')}. Anything added
              below is searched as well, not instead.
            </Typography>
          </Alert>
        )}
        {/*
          Switching to "I'll name them" used to ask first and then take the
          emphasis off, because an emphasis left on would go on choosing
          sites — which is the one thing that mode promises not to do.

          The emphasis is kept now, and what it chooses is what stops instead:
          `maxStops` below says the round holds exactly the named places, so
          nothing is suggested from the emphasis's kinds. Everything ELSE the
          emphasis decides — the ground it keeps clear of, the roads it
          prefers, how it weighs a path — still applies, which is what makes
          keeping it worth doing rather than merely less work than a dialog.
        */}
        <SitesModeSwitch mode={sitesMode} onChange={setSitesMode} />

        {/*
          How long the round has, beside the stops it is a length for.

          Offered in the kinds mode, and only once at least one kind of site
          is in force. That pairing is the whole of it: a length is a ceiling
          rather than a target, so with nothing for the planner to draw from
          it cannot produce a single stop, and five hours and two give the
          same drive. Under "I’ll name them" the stops are already decided,
          and a length there reads as a competing instruction.

          The value is kept, not cleared, while the field is away, so taking a
          kind out and putting it back brings the same number with it.
        */}
        {sitesMode === 'kinds' && budgetDecides && (
          <DurationField
            purpose={'How much total time is available for the round, including both '
              + 'travel and stops. The planner uses this to decide how many sites can '
              + 'reasonably fit.'}
            minutes={availableMinutes}
            onChange={onAvailableMinutesChange}
            sx={{ mb: 1 }}
          />
        )}

        {sitesMode === 'kinds' && (<>
        {/*
          What the round draws its sites from, and what it favours among them.

          This was a single-select that narrowed the search box below it and
          nothing else, which is not what a field at the top of "Sites to
          visit" reads as. It now sets the interests the planner actually
          uses — and still narrows the search, since a search restricted to
          what the round is for is the more useful of the two behaviours.
        */}
        <CategoryChoice
          /*
            Not the kinds this round is already keeping clear of.
            
            Visiting and avoiding the same kind are two instructions that
            cancel, and the planner obeys the exclusion — so the round comes
            back without the thing it was for, and nothing says which answer
            won. The avoidance side refuses the same pairing from its end;
            this is the other half, so the conflict cannot be reached from
            either direction. Stop avoiding it and it reappears here.
          */
          categories={categories.filter(
            (c) => c.isVisitable && !avoidCategories.includes(c.key),
          )}
          value={interestKeys}
          onChange={(keys) => {
            onInterestKeysChange?.(keys);
            // A time given to a kind no longer drawn from is not a setting,
            // it is a leftover — and it would come back if the kind did.
            setCategoryDwell((current) =>
              Object.fromEntries(
                Object.entries(current).filter(([key]) => keys.includes(key)),
              ));
          }}
          dwellMinutes={categoryDwell}
          onDwellChange={(key, minutes) => setCategoryDwell((current) => {
            const next = { ...current };
            if (minutes === null) delete next[key];
            else next[key] = minutes;
            return next;
          })}
        />
        <BoostChoice
          categories={categories}
          chosen={interestKeys}
          value={boostKeys}
          onChange={setBoostKeys}
        />
        </>)}

        {/*
          Offered in the kinds mode, and the whole point of the other.
        */}
        {/*
          Offered once there is something to add TO.

          With no kinds chosen, "Choose for me" has nothing to choose from,
          and "also visit places I name" is an addition to an empty round.
          The switch appears with the first kind, beside the thing it extends.
        */}
        {sitesMode === 'kinds' && budgetDecides && (
          <FormControlLabel
            sx={{ m: 0, mt: 0.5 }}
            control={(
              <Switch
                size="small"
                checked={showNaming}
                disabled={mustVisit.length > 0 || visitPins.length > 0}
                onChange={(event) => setAlsoNaming(event.target.checked)}
              />
            )}
            label={(
              <Typography variant="body2">
                Also visit places I name
              </Typography>
            )}
          />
        )}

        {showNaming && (<>
        {/*
          Naming a place works in both modes.

          "Choose for me" means the planner fills the round; it never meant
          it fills it alone. Somebody who wants museums AND one particular
          restaurant had no way to say so, and a place added from the map's
          own panel already landed in this list and was already sent — the
          search for one was the only part missing.

          The modes still differ where it counts: the named mode sends no
          kinds at all, so nothing arrives that was not asked for by name.
        */}
        <PinChips
          pins={visitPins}
          onChange={onVisitPinsChange}
          colour="primary"
        />
        <PlacePicker
          cityId={city.id}
          allCities
          label="Which place to add?"
          placeholder={
            interestKeys.length > 0 ? 'Search within those kinds' : 'Search any place'}
          visitableOnly
          {...(interestKeys.length > 0 ? { categoryKeys: interestKeys } : {})}
          value={mustVisit}
          onChange={onMustVisitChange}
          /*
            The round's own two ends. A stop is somewhere it passes between
            them, so either of them as a stop is a second arrival at a place
            the route is already at.
          */
          reserved={[
            ...(start ? [{ id: start.id, why: 'This is where the round starts' }] : []),
            ...(end ? [{ id: end.id, why: 'This is where the round ends' }] : []),
          ]}
          onExternalPick={(place) => onExternalPick(place, 'visit')}
        />
        </>)}

        {/*
          One row per chosen place, rather than one number for all of them.

          A cathedral and a viewpoint are both places to visit and are not
          both twenty minutes. Blank takes the category's own default — which
          is what nearly every place should use — so this list is a way of
          saying "except this one", not a set of fields to fill in.
        */}
        <StayRows
          places={mustVisit}
          pins={visitPins}
          categories={categories}
          stopDwellMinutes={stopDwellMinutes}
          pinnedFallback={defaultStopMinutes}
          colourFor={colourFor}
          onPlaceChange={onStopDwellChange}
          onPinChange={(index, minutes) => onVisitPinsChange(visitPins.map((pin, i) => {
            if (i !== index) return pin;
            const { dwellMinutes: _drop, ...rest } = pin;
            return minutes === null ? rest : { ...rest, dwellMinutes: minutes };
          }))}
          onRemovePlace={(poiId) => {
            onStopDwellChange(poiId, null);
            onMustVisitChange(mustVisit.filter((p) => p.id !== poiId));
          }}
          onRemovePin={(index) => onVisitPinsChange(visitPins.filter((_, i) => i !== index))}
          onShowDetails={onShowDetails}
        />

        {/*
          Offered wherever a place is being named by hand.

          It used to be the named mode alone, on the argument that in "Choose
          for me" the planner is already picking from the kinds above and a
          second ranked list adds nothing but height. True of the kinds, and
          beside the point once "Also visit places I name" is on: that switch
          opens a picker that expects a place BY NAME, which is the one thing
          somebody looking for a stop does not have. The suggestions are what
          answer "what is along here?", and they belong with the picker they
          fill in rather than with the mode that happens to be showing.

          `showNaming` is the condition because it is already the one the
          picker uses — the named mode, the switch, or something named
          already — so the two cannot come apart.
        */}
        {showNaming && (
        <>
        {/*
          What the corridor offers.

          The same ranking the planner uses, stopped short of the optimiser —
          so a traveller sees what was on offer rather than only what survived
          being fitted into a day.

          Always here, asked for by a button. It used to appear only once both
          ends were set: "on the way" does mean nothing without a way, but
          somebody who opens this section looking for suggestions then finds an
          empty box and no reason for it. `ready` moves that condition onto the
          button, which can say what is missing.
        */}
        <StopRecommendations
          state={recommendations}
          ready={Boolean(draft)}
          cityId={city.id}
          categories={categories.filter((c) => c.isVisitable)}
          chosenIds={mustVisit.map((p) => p.id)}
          onAddStop={(poi) => {
            if (!mustVisit.some((p) => p.id === poi.id)) {
              onMustVisitChange([...mustVisit, poi]);
            }
          }}
          onShowDetails={onShowDetails}
        />
        </>
        )}

      </FieldGroup>

      <FieldGroup
        tour="avoid"
        defaultExpanded={avoidCategories.length > 0 || avoid.length > 0}
        title="Exclusion zones"
        count={avoidCategories.length + avoid.length + avoidPins.length}
        caption={
          mode === 'transit'
            ? 'Transit follows fixed lines — avoidance is advisory, not guaranteed'
            : 'The route holds the distance below from every one of these'
        }
        tone="warning"
      >
        {/*
          The same for what it keeps clear of. Only when it keeps clear of
          something — an emphasis with no exclusions of its own has nothing
          to say here, and a line saying "nothing" is noise in a box that is
          already coloured for attention.
        */}
        {chosenTheme && chosenTheme.avoidCategoryKeys.length > 0 && (
          <Alert severity="info" variant="outlined" sx={{ mb: 1.5, py: 0.5 }}>
            <Typography variant="caption">
              {themeName(chosenTheme.key, chosenTheme.label)} already holds clear
              of {chosenTheme.avoidCategoryKeys.map(labelFor).join(', ')}
              {chosenTheme.avoidDistanceM ? ` at ${chosenTheme.avoidDistanceM} m` : ''}
              . Anything added below is held clear of too.
            </Typography>
          </Alert>
        )}

        <AvoidancePanel
          cityId={city.id}
          onShowDetails={onShowDetails}
          // Counted over the ground avoidance covers, not the ground you can
          // browse — see the prop's own note.
          categories={avoidCategoryCounts?.length ? avoidCategoryCounts : categories}
          filter={avoidFilter}
          onFilterChange={setAvoidFilter}
          avoid={avoid}
          onAvoidChange={onAvoidChange}
          avoidCategories={avoidCategories}
          onAvoidCategoriesChange={onAvoidCategoriesChange}
          // So the same kind cannot be both visited and avoided.
          visitingCategories={interestKeys}
          avoidDistanceM={avoidDistanceM}
          onAvoidDistanceChange={onAvoidDistanceChange}
          colourFor={colourFor}
          avoidDistanceByPoiId={avoidDistanceByPoiId}
          avoidDistanceByCategoryKey={avoidDistanceByCategoryKey}
          onAvoidDistanceOverride={onAvoidDistanceOverride}
          pinRow={
            <PinChips pins={avoidPins} onChange={onAvoidPinsChange} colour="warning" />
          }
          onExternalPick={(place) => onExternalPick(place, 'avoid')}
        />
      </FieldGroup>

      {/* ── extras ───────────────────────────────────────────────── */}
      <Accordion data-tour="extras" variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
            <Typography variant="body2" sx={{ flex: 1 }}>Rest stops and constraints</Typography>
            {(Number(lunch) + Number(coffee)) > 0 && (
              <Chip size="small" variant="outlined" label={Number(lunch) + Number(coffee)} />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            {/*
              The toggle owns its own row and the two fields sit beneath it.
              Three controls abreast does not fit the sidebar: the label wraps
              and both fields end up clipped.
            */}
            <BreakRow
              label="Lunch stop"
              enabled={lunch}
              onEnabledChange={(on) => onBreaksEnabledChange('lunch', on)}
              at={lunchAt}
              onAtChange={setLunchAt}
              minutes={breakMinutes.lunch}
              onMinutesChange={(minutes) => onBreakMinutesChange('lunch', minutes)}
              cityId={city.id}
              categoryKeys={BREAK_CATEGORIES.lunch}
              place={breakPlaces.lunch}
              onPlaceChange={(poi) => onBreakPlaceChange('lunch', poi)}
              role="lunch"
              pinned={lunchPinned}
              onPinnedChange={setLunchPinned}
              mode={breakMode.lunch}
              onModeChange={(next) => setBreakMode((c) => ({ ...c, lunch: next }))}
              recommendations={recommendations}
              canShowOnMap={Boolean(onSuggestionPreview)}
            canSuggest={Boolean(draft)}
              onShowDetails={onShowDetails}
              placeholder="Search restaurants"
            />

            <BreakRow
              label="Coffee break"
              enabled={coffee}
              onEnabledChange={(on) => onBreaksEnabledChange('coffee', on)}
              at={coffeeAt}
              onAtChange={setCoffeeAt}
              minutes={breakMinutes.coffee}
              onMinutesChange={(minutes) => onBreakMinutesChange('coffee', minutes)}
              cityId={city.id}
              categoryKeys={BREAK_CATEGORIES.coffee}
              place={breakPlaces.coffee}
              onPlaceChange={(poi) => onBreakPlaceChange('coffee', poi)}
              role="coffee"
              pinned={coffeePinned}
              onPinnedChange={setCoffeePinned}
              mode={breakMode.coffee}
              onModeChange={(next) => setBreakMode((c) => ({ ...c, coffee: next }))}
              recommendations={recommendations}
              canShowOnMap={Boolean(onSuggestionPreview)}
            canSuggest={Boolean(draft)}
              onShowDetails={onShowDetails}
              placeholder="Search cafes"
            />

            {/*
              Tolls are answered in "How this one travels", not here.

              That section already asks about toll roads alongside main roads,
              residential streets and ferries, on the same three-way Auto /
              Avoid / Prefer the rest of them use. A second switch for the
              same road, in a section about rest stops, was two places to say
              one thing — and two places to disagree.

              `avoidTolls` is still read from a seed and still sent, so a
              saved route or an emphasis carrying it plans exactly as before.
            */}

            {/*
              Whether the proximity score is shown is an Admin setting, not a
              per-round one. It measures how close the stops are to hospitals,
              police and fire stations — a property of the deployment's remit
              rather than of one tasking, and a switch here invited it to be
              turned on and off mid-plan as though it changed the route. It
              changes nothing but whether the number is reported.
            */}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/*
        How this one travels.

        Last, and shut. An emphasis answers these for every round of its kind
        and most journeys want whatever it says — or, with no emphasis, the
        router's own judgement. This is for the journey that wants something
        else: everything reads "Auto" until somebody changes it, and nothing
        is sent until they do.
      */}
      <Accordion variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {chosenTheme
                ? `How this one travels — ${themeName(chosenTheme.key, chosenTheme.label)} decides`
                : 'How this one travels'}
            </Typography>
            {roadAnswers > 0 && (
              <Chip size="small" variant="outlined" label={roadAnswers} />
            )}
            {/*
              Offered only once something differs from the emphasis, because a
              control that restores what is already there reads as a warning
              that something is wrong. Stops the click reaching the accordion
              underneath, which would otherwise fold the panel shut on the
              values it just put back.
            */}
            {themeEdited && (
              <Button
                size="small"
                onClick={(event) => { event.stopPropagation(); restoreTheme(); }}
                sx={{ py: 0, minWidth: 0 }}
              >
                Restore to default
              </Button>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
          <JourneyCharacterFields
            theme={chosenTheme}
            roadOptions={roadOptions}
            onRoadOptionsChange={setRoadOptions}
            characteristics={characteristics}
            onCharacteristicsChange={setCharacteristics}
            appliesTo="this journey"
            /*
             * So a characteristic that asks to stay away from something can
             * offer the strong form of the same wish: avoiding the categories
             * outright, which the router can act on, where the characteristic
             * alone can only pick between lines it was already given.
             */
            avoidCategories={avoidCategories}
            onAvoidCategoriesChange={onAvoidCategoriesChange}
          />

            {SHOW_PATH_NUMBERS && (
            <>
            {/*
              What the three sliders above are worth, in time and in metres.
              Empty is the emphasis's figure, or the site-wide one where there
              is no emphasis.

              Said out loud, and all five of them. Three appeared here as bare
              labels — "May be longer by", with no heading and no hint — so
              what they governed was anybody's guess; and the two that decide
              how far an alternative has to keep from the route it is an
              alternative TO were only ever editable on an emphasis, though
              the planner reads them from a request just the same. The wording
              is the emphasis editor's, because the same number should not be
              explained two ways.
            */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                The numbers behind it
              </Typography>
              <Typography variant="caption" color="text.secondary">
                What the three above are allowed to cost. Leave a field empty
                for the emphasis&rsquo;s figure, or the site-wide one.
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
              }}
            >
              {([
                ['extraPct', 'May be longer by', '%',
                  'How much longer than the quickest a better line may be.'],
                ['extraMinutes', 'Plus slack of', 'min',
                  'So a short run is not decided by rounding.'],
                ['roadRadiusM', 'Counts as passed within', 'm',
                  'How far off the road a place still counts.'],
                ['differentPathBufferM', 'Keep off a compared route by', 'm',
                  'Width of the band that forces another road.'],
                ['differentPathKeepClearM', 'Leaving each end open for', 'm',
                  'So the stops on it stay reachable.'],
              ] as const).map(([key, label, unit, hint]) => (
                <TextField
                  key={key}
                  size="small"
                  type="number"
                  label={label}
                  placeholder={
                    chosenTheme?.pathOptions?.[key] !== undefined
                      ? String(chosenTheme.pathOptions[key])
                      : 'Auto'
                  }
                  value={pathNumbers[key] ?? ''}
                  onChange={(e) => setPathNumbers((current) => ({
                    ...current, [key]: e.target.value,
                  }))}
                  helperText={hint}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
                    },
                  }}
                />
              ))}
            </Box>
            </>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/*
        Said above the button, where it stops somebody pressing it, rather
        than as a failure after the wait.
      */}
      {placeProblems.length > 0 && (
        <Alert severity="warning" variant="outlined">
          {placeProblems.map((problem) => (
            <Typography key={problem} variant="body2">{problem}</Typography>
          ))}
        </Alert>
      )}

      <Button
        data-tour="plan"
        variant="contained"
        size="large"
        onClick={submit}
        disabled={!canSubmit || placeProblems.length > 0}
      >
        {submitting ? 'Planning…' : 'Plan the route'}
      </Button>

      {/* Offered only once there is something to clear. */}
      {onReset && (start || startPin || end || endPin || mustVisit.length > 0
        || avoid.length > 0 || visitPins.length > 0 || avoidPins.length > 0) && (
        <Button size="small" color="inherit" onClick={onReset}>
          Clear
        </Button>
      )}

      {!draft && (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {returnToStart
            ? 'Choose a starting point to begin'
            : 'Choose a departure point and objective to begin'}
        </Typography>
      )}
    </Stack>
  );
}


/**
 * A titled, boxed group of related fields.
 *
 * The visit and avoid sections use the same two controls, so without a
 * heading and a visible boundary the second pair reads as a duplicate of the
 * first and neither says which list it feeds.
 */
/**
 * A titled group of fields, folded away until it is wanted.
 *
 * Both of these are long — two near-identical controls each, a chip list that
 * grows with every choice, and a distance slider — and neither is needed on
 * most rounds: an emphasis already says what the round draws from, and plenty
 * of taskings avoid nothing at all. Open, they pushed the departure point,
 * the times and "Plan the route" below the fold on every visit.
 *
 * A group that already holds something opens itself, so a setting somebody
 * made is never hidden behind a fold they have to know to look behind.
 */
/**
 * A start or destination: either a place from the database, or the browser's
 * reported position shown as a removable chip.
 */
function Endpoint({
  label, placeholder, cityId, value, onChange, here, onClearHere, onUseHere, locating,
  onExternalPick,
}: {
  label: string;
  placeholder: string;
  cityId: string;
  value: Poi | null;
  onChange: (poi: Poi | null) => void;
  here: PinnedPoint | null;
  onClearHere: () => void;
  onUseHere: () => void;
  locating: boolean;
  onExternalPick?: (place: { name: string; lat: number; lng: number }) => void;
}) {
  return (
    <Stack data-tour="endpoints" direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {here ? (
          <Chip
            color="primary"
            onDelete={onClearHere}
            sx={{ height: 40, borderRadius: 1, width: '100%', justifyContent: 'space-between' }}
            label={`${label}: ${here.label ?? 'pinned'} (${here.lat.toFixed(4)}, ${here.lng.toFixed(4)})`}
          />
        ) : (
          <PlaceAutocomplete
            cityId={cityId}
            /*
             * Every city, not the one selected above.
             *
             * Routing crosses city lines from one shared tile set, so a round
             * can begin in Washington DC and end in Washington state. The
             * city above still chooses what the map is looking at and biases
             * the provider's own search; it was never a reason to hide half
             * the places from the pickers.
             */
            allCities
            label={label}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            {...(onExternalPick ? { onExternalPick } : {})}
          />
        )}
      </Box>
      <Tooltip title={`Use my current location as ${label.toLowerCase()}`}>
        <span>
          <IconButton
            size="small"
            onClick={onUseHere}
            disabled={locating}
            color={here ? 'primary' : 'default'}
            sx={{ mt: 0.5 }}
          >
            {locating ? <CircularProgress size={18} /> : <MyLocationIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

/** Spots marked on the map, shown so they can be removed like any other. */
/** Spots marked on the map, listed where the form lists their kind. */
export function PinChips({
  pins, onChange, colour,
}: {
  pins: PinnedPoint[];
  onChange: (pins: PinnedPoint[]) => void;
  colour: 'primary' | 'warning';
}) {
  if (pins.length === 0) return null;

  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }} useFlexGap>
      {pins.map((pin, index) => (
        <Chip
          key={`${pin.lat},${pin.lng},${index}`}
          size="small"
          color={colour}
          label={pin.label ?? `${pin.lat.toFixed(4)}, ${pin.lng.toFixed(4)}`}
          onDelete={() => onChange(pins.filter((_, i) => i !== index))}
        />
      ))}
    </Stack>
  );
}
